import React, { useState, useEffect } from 'react';
import { FileText, CheckCircle, Users, BarChart3 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import Card3D from '../admindashboard/Card3D.js';
import AnimatedNumber from '../admindashboard/AnimatedNumber.js';

export default function MPSDashboard({ axiosInstance }) {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/projects');
        setProjects(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.warn('[MPSDashboard] Failed to fetch projects:', err?.message || err);
        setProjects([]);
      }
    })();
  }, [axiosInstance]);

  const totalProjects = projects.length;
  const completedProjects = projects.filter(p => p.status === 'Completed').length;
  const inProgressProjects = projects.filter(p => p.status === 'In Progress').length;
  const completionRate = totalProjects > 0 ? Math.round((completedProjects / totalProjects) * 100) : 0;

  const currentDateTime = new Date().toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const statCards = [
    {
      label: 'Total Projects',
      value: totalProjects,
      Icon: FileText,
      ring: 'rgba(59,130,246,0.55)',
      bg: 'linear-gradient(135deg, rgba(59,130,246,0.14), rgba(59,130,246,0.04))',
    },
    {
      label: 'Completed',
      value: completedProjects,
      Icon: CheckCircle,
      ring: 'rgba(34,197,94,0.55)',
      bg: 'linear-gradient(135deg, rgba(34,197,94,0.14), rgba(34,197,94,0.04))',
    },
    {
      label: 'In Progress',
      value: inProgressProjects,
      Icon: Users,
      ring: 'rgba(249,115,22,0.55)',
      bg: 'linear-gradient(135deg, rgba(249,115,22,0.14), rgba(249,115,22,0.04))',
    },
    {
      label: 'Completion Rate',
      value: `${completionRate}%`,
      Icon: BarChart3,
      ring: 'rgba(168,85,247,0.55)',
      bg: 'linear-gradient(135deg, rgba(168,85,247,0.14), rgba(168,85,247,0.04))',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--nad-t1)' }}>
            Project Monitoring Overview
          </h2>
          <p className="mt-1 text-sm" style={{ color: 'var(--nad-t3)' }}>
            High-level snapshot of active projects, operations, and resource allocation.
          </p>
        </div>
        <div className="text-xs" style={{ color: 'var(--nad-t4)' }}>
          {currentDateTime}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {statCards.map((card, i) => {
          const { Icon, label, value, ring, bg } = card;
          return (
            <Card3D
              key={label}
              intensity={6}
              lift={14}
              className="stagger-in rounded-2xl overflow-hidden"
              style={{ '--i': i }}
            >
              <div
                className="p-5"
                style={{
                  background: 'var(--nad-content-bg)',
                  border: '1px solid var(--nad-content-border)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  minHeight: '100%',
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--nad-t4)' }}>
                      {label}
                    </p>
                    <p className="mt-2 text-3xl font-bold tracking-tight" style={{ color: 'var(--nad-t1)' }}>
                      <AnimatedNumber
                        value={String(value)}
                        delay={400 + i * 120}
                        duration={1300}
                      />
                    </p>
                  </div>
                  <div
                    className="relative flex-shrink-0 size-12 rounded-xl flex items-center justify-center"
                    style={{ background: bg, boxShadow: `inset 0 0 0 1px ${ring}` }}
                  >
                    <Icon size={22} style={{ color: ring }} />
                  </div>
                </div>
                <div
                  className="mt-4 h-1.5 rounded-full overflow-hidden"
                  style={{ background: 'var(--nad-s1)' }}
                >
                  <div
                    className="h-full rounded-full shimmer-bg"
                    style={{
                      width:
                        label === 'Completion Rate'
                          ? `${Math.min(100, completionRate)}%`
                          : label === 'Total Projects'
                          ? '100%'
                          : totalProjects > 0
                          ? `${Math.min(100, Math.round((((label === 'Completed' ? completedProjects : inProgressProjects) / totalProjects) * 100)))}%`
                          : '0%',
                      background: `linear-gradient(90deg, ${ring}, var(--nad-accent-txt))`,
                    }}
                  />
                </div>
              </div>
            </Card3D>
          );
        })}
      </div>
    </div>
  );
}
